<script>
    import {onMount} from 'svelte';

    import Tooltip from 'flowbite/lib/esm/components/tooltip/index';

    import I18n from '@js/modules/translations';
    import Notification from '@js/components/layouts/notification';

    import {favoriteStore} from '@js/stores/favoriteStore';
    import {collectionsStore} from '@js/stores/collectionsStore';
    import {
        fetchRideCollectionIds,
        addRideToCollection,
        removeRideFromCollection
    } from '@js/actions/collectionActions';
    import {isSessionLost, reconnect} from '@js/actions/accountActions';

    import Button from '@js/components/themes/Button.svelte';
    import Icon from '@js/components/themes/Icon.svelte';

    import heartIcon from '@js/components/icons/heartIcon';
    import heartOutlineIcon from '@js/components/icons/heartOutlineIcon';

    const {
        rideId,
        variant = 'overlay'
    } = $props();

    const id = $derived(Number(rideId));

    const isFavorited = $derived($favoriteStore.has(id));

    const label = $derived(isFavorited
        ? I18n.t('js.ride.favorites.button.remove')
        : I18n.t('js.ride.favorites.button.add'));

    const canChoose = $derived(variant === 'action' && $collectionsStore.length > 1);

    let isPickerOpen = $state(false);
    let isPickerLoading = $state(false);
    let memberIds = $state(new Set());

    const _replaceMembers = (mutator) => {
        const next = new Set(memberIds);
        mutator(next);
        memberIds = next;
    };

    const _syncHeart = () => {
        if (memberIds.size > 0) {
            favoriteStore.remember(id);
        } else {
            favoriteStore.forget(id);
        }
    };

    const _handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!canChoose) {
            favoriteStore.toggle(id);
            return;
        }

        isPickerOpen = !isPickerOpen;
        if (!isPickerOpen) return;

        isPickerLoading = true;
        fetchRideCollectionIds(id)
            .then((response) => {
                memberIds = new Set((response?.collectionIds || []).map(Number));
            })
            .finally(() => {
                isPickerLoading = false;
            });
    };

    const _toggleCollection = (collection, event) => {
        event.preventDefault();
        event.stopPropagation();

        _toggleMembership(collection);
    };

    const _toggleMembership = (collection) => {
        const isMember = memberIds.has(collection.id);

        _replaceMembers((ids) => (isMember ? ids.delete(collection.id) : ids.add(collection.id)));

        _syncHeart();

        const request = isMember
            ? removeRideFromCollection(collection.id, id)
            : addRideToCollection(collection.id, id);

        request.then((response) => {
            if (response?.errors) {
                _replaceMembers((ids) => (isMember ? ids.add(collection.id) : ids.delete(collection.id)));
                _syncHeart();

                if (isSessionLost(response)) {
                    reconnect('session_reconnect', () => _toggleMembership(collection), {});
                    return;
                }

                Notification.error(I18n.t('js.ride.favorites.collections.error'));
            }
        });
    };

    onMount(() => {
        favoriteStore.hydrate();

        if (variant === 'action') {
            collectionsStore.hydrate();

            const tooltipElement = document.getElementById('tooltip-favorite-ride');
            const tooltipTrigger = document.getElementById('favorite-ride');

            if (tooltipElement && tooltipTrigger) {
                new Tooltip(tooltipElement, tooltipTrigger, {
                    placement: 'top',
                    triggerType: 'hover'
                });
            }
        }
    });
</script>

{#if variant === 'action'}
    <div class="relative inline-block">
        <Button id="favorite-ride"
                class="block"
                color="primary-light"
                rounded={true}
                onClick={_handleClick}
                aria-pressed={isFavorited}
                aria-label={label}
                data-tooltip-target="tooltip-favorite-ride">
            <Icon class={`w-5 h-5 ${isFavorited ? 'text-red-600' : ''}`}
                  icon={isFavorited ? heartIcon : heartOutlineIcon}/>
        </Button>

        {#if isPickerOpen && canChoose}
            <button type="button"
                    class="fixed inset-0 z-10 cursor-default"
                    aria-label={I18n.t('js.ride.favorites.collections.close')}
                    onclick={(event) => { event.preventDefault(); event.stopPropagation(); isPickerOpen = false; }}></button>

            <div class="absolute left-0 mt-2 w-56 max-h-64 overflow-y-auto bg-white rounded-xl shadow-2xl border border-gray-100 z-20 py-2"
                 role="menu">
                <p class="px-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    {I18n.t('js.ride.favorites.collections.add_to')}
                </p>

                {#if isPickerLoading}
                    <p class="px-3 py-2 text-sm text-gray-400">…</p>
                {:else}
                    {#each $collectionsStore as collection (collection.id)}
                        <button type="button"
                                class="flex items-center justify-between w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                                role="menuitemcheckbox"
                                aria-checked={memberIds.has(collection.id)}
                                onclick={(event) => _toggleCollection(collection, event)}>
                            <span class="truncate">{collection.name}</span>
                            {#if memberIds.has(collection.id)}
                                <svg class="w-4 h-4 text-primary-800 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                                </svg>
                            {/if}
                        </button>
                    {/each}
                {/if}
            </div>
        {/if}
    </div>

    <div id="tooltip-favorite-ride"
         role="tooltip"
         class="absolute inline-block px-3 py-2 invisible text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 z-10 tooltip">
        {label}
        <div class="tooltip-arrow" data-popper-arrow></div>
    </div>
{:else}
    <button type="button"
            class="inline-flex items-center justify-center w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full shadow-lg cursor-pointer transition duration-200 hover:scale-110 focus:outline-hidden focus:ring-2 focus:ring-primary-300"
            onclick={_handleClick}
            aria-pressed={isFavorited}
            aria-label={label}
            title={label}>
        <Icon class={`w-4 h-4 transition-colors ${isFavorited ? 'text-red-500' : 'text-gray-600'}`}
              icon={isFavorited ? heartIcon : heartOutlineIcon}/>
    </button>
{/if}
